import axios from '../utils/axiosConfig';
import { ELibrary, ApiResponse, ApiListResponse } from '../types/contentManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class ELibraryService {
  private baseURL = `${API_BASE_URL}/e-library`;

  // Get all E-Library items
  async getAllLibraries(includeHidden: boolean = false, category?: string): Promise<ELibrary[]> {
    try {
      let url = this.baseURL;
      const params = new URLSearchParams();
      
      if (includeHidden) params.append('includeHidden', 'true');
      if (category) params.append('category', category);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get<ApiListResponse<ELibrary>>(url);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch e-library items');
    } catch (error) {
      console.error('Error fetching e-library items:', error);
      throw error;
    }
  }

  // Get visible E-Library items (for homepage)
  async getVisibleLibraries(category?: string): Promise<ELibrary[]> {
    return this.getAllLibraries(false, category);
  }

  // Get libraries by category
  async getLibrariesByCategory(category: string, includeHidden: boolean = false): Promise<ELibrary[]> {
    return this.getAllLibraries(includeHidden, category);
  }

  // Get E-Library item by ID
  async getLibraryById(id: number): Promise<ELibrary> {
    try {
      const response = await axios.get<ApiResponse<ELibrary>>(`${this.baseURL}/${id}`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch e-library item');
    } catch (error) {
      console.error('Error fetching e-library item:', error);
      throw error;
    }
  }

  // Create new E-Library item
  async createLibrary(formData: FormData): Promise<ELibrary> {
    try {
      const response = await axios.post<ApiResponse<ELibrary>>(this.baseURL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create e-library item');
    } catch (error) {
      console.error('Error creating e-library item:', error);
      throw error;
    }
  }

  // Update E-Library item
  async updateLibrary(id: number, formData: FormData): Promise<ELibrary> {
    try {
      const response = await axios.put<ApiResponse<ELibrary>>(`${this.baseURL}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update e-library item');
    } catch (error) {
      console.error('Error updating e-library item:', error);
      throw error;
    }
  }

  // Delete E-Library item
  async deleteLibrary(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<null>>(`${this.baseURL}/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete e-library item');
      }
    } catch (error) {
      console.error('Error deleting e-library item:', error);
      throw error;
    }
  }

  // Toggle visibility
  async toggleVisibility(id: number): Promise<ELibrary> {
    try {
      const response = await axios.patch<ApiResponse<ELibrary>>(`${this.baseURL}/${id}/toggle-visibility`);
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to toggle visibility');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  }

  // Helper method to get file URL
  getFileUrl(filePath: string | undefined | null): string {
    if (!filePath || typeof filePath !== 'string') {
      return '';
    }
    
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    if (filePath.startsWith('/uploads/')) {
      return `${API_BASE_URL.replace('/api', '')}${filePath}`;
    }
    
    return `${API_BASE_URL.replace('/api', '')}/uploads/${filePath}`;
  }

  // Get icon for library item type
  getLibraryIcon(hasFile: boolean): string {
    return hasFile ? '📄' : '🔗';
  }

  // Get category badge color
  getCategoryColor(color: string | undefined): string {
    return color || '#95a5a6';
  }
}

export default new ELibraryService();
